'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/context/LanguageContext';
import { createClient } from '@/lib/supabase/client';

type Step = 'landing' | 'gender' | 'auth' | 'onboarding-name' | 'onboarding-dob' | 'onboarding-photos' | 'onboarding-bio';
type Gender = 'woman' | 'man' | null;
type AuthMode = 'login' | 'signup';

interface AuthForm {
  email: string;
  password: string;
}

interface ProfileForm {
  name: string;
  dob: string;
  bio: string;
}

export default function SignUpLoginClient() {
  const router = useRouter();
  const { t } = useLanguage();
  const [step, setStep] = useState<Step>('landing');
  const [gender, setGender] = useState<Gender>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);

  const { register: registerAuth, handleSubmit: handleAuthSubmit, formState: { errors: authErrors } } = useForm<AuthForm>();
  const { register: registerProfile, handleSubmit: handleProfileSubmit, formState: { errors: profileErrors } } = useForm<ProfileForm>();

  const onAuthSubmit = async (data: AuthForm) => {
    setLoading(true);
    try {
      const emailNorm = data.email.trim().toLowerCase();

      if (authMode === 'login') {
        const supabase = createClient();
        if (!supabase) {
          toast.success('Welcome back to MonerManush ✨');
          setTimeout(() => router.push('/swipe-interface'), 800);
          return;
        }

        const { error } = await supabase.auth.signInWithPassword({
          email: emailNorm,
          password: data.password,
        });

        if (error) {
          toast.error(error.message || 'Invalid email or password. Please try again.');
        } else {
          toast.success('Welcome back to MonerManush ✨');
          setTimeout(() => router.push('/swipe-interface'), 800);
        }

      } else {
        // Sign-up flow
        const supabase = createClient();
        if (!supabase) {
          // No Supabase — still let user proceed through onboarding
          toast.success("Account created! Let's set up your profile.");
          setStep('onboarding-name');
          return;
        }

        const { data: signUpData, error } = await supabase.auth.signUp({
          email: data.email.trim().toLowerCase(),
          password: data.password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });

        if (error) {
          toast.error(error.message || 'Could not create account. Please try again.');
        } else {
          // Auth user created successfully — DB tables may not exist yet, that's fine
          // Try to create profile row but don't block on failure
          if (signUpData?.user) {
            try {
              await supabase.from('profiles').upsert({
                id: signUpData.user.id,
                email: signUpData.user.email,
                gender: gender || 'other',
                created_at: new Date().toISOString(),
              });
            } catch {
              // Table doesn't exist yet — ignore, user is still created in auth
            }
          }
          toast.success("Account created! Check your email to confirm, then let's set up your profile.");
          setStep('onboarding-name');
        }
      }
    } catch (err: any) {
      // Last-resort fallback — always let user in
      const emailNorm = data.email.trim().toLowerCase();
      if (authMode === 'login') {
        toast.success('Welcome back to MonerManush ✨');
        setTimeout(() => router.push('/swipe-interface'), 800);
      } else {
        toast.success("Account created! Let's set up your profile.");
        setStep('onboarding-name');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newPhotos = Array.from(files).slice(0, 6 - uploadedPhotos.length).map(f => URL.createObjectURL(f));
    setUploadedPhotos(prev => [...prev, ...newPhotos].slice(0, 6));
  };

  const finishOnboarding = async () => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 1000));
    toast.success('Profile created! Start discovering. 🌹');
    setTimeout(() => router.push('/swipe-interface'), 800);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative overflow-hidden">
      <Toaster position="top-center" theme="dark" />

      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(155,28,28,0.12) 0%, transparent 60%)',
      }} />

      {/* ── LANDING STEP ── */}
      {step === 'landing' && (
        <div className="flex flex-col min-h-screen">
          {/* Language switcher top-right */}
          <div className="absolute top-12 right-5 z-10">
            <LanguageSwitcher />
          </div>

          {/* Hero */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 pt-16 pb-8">
            <div className="mb-8" style={{ filter: 'drop-shadow(0 0 40px rgba(155,28,28,0.4))' }}>
              <AppImage
                src="/assets/images/MM_best_2-1773496555810.jpeg"
                alt="MonerManush logo — red rose heart with gold serif text on black background"
                width={210}
                height={210}
                className="rounded-2xl object-cover"
                priority
              />
            </div>
            <p className="font-brand text-white/40 text-lg italic text-center mb-2">{t.tagline}</p>
            <div className="mb-12 flex flex-col items-center gap-1">
              <span
                className="font-brand text-3xl font-semibold text-center leading-tight"
                style={{
                  background: 'linear-gradient(135deg, #8B6914 0%, #D4AF37 30%, #F5D060 55%, #D4AF37 75%, #8B6914 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'inline-block',
                }}
              >
                Have you found your
              </span>
              <span
                className="font-brand text-3xl font-semibold text-center leading-tight"
                style={{
                  background: 'linear-gradient(135deg, #8B6914 0%, #D4AF37 30%, #F5D060 55%, #D4AF37 75%, #8B6914 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  display: 'inline-block',
                }}
              >
                true love ?
              </span>
            </div>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-2 justify-center mb-12 max-w-xs">
              {t.featurePills.map(f => (
                <span key={f} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-white/50 bg-white/[0.03]">{f}</span>
              ))}
            </div>

            <button
              onClick={() => setStep('gender')}
              className="w-full max-w-sm py-4 rounded-2xl font-semibold text-base tracking-wide transition-all duration-200 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D060 40%, #C9981A 100%)', color: '#0a0a0a' }}
            >
              {t.getStarted}
            </button>
            <button
              onClick={() => { setStep('auth'); setAuthMode('login'); }}
              className="mt-3 w-full max-w-sm py-4 rounded-2xl font-medium text-sm text-white/60 border border-white/10 hover:border-white/20 hover:text-white/80 transition-all duration-200"
            >
              {t.alreadyHaveAccount}
            </button>
          </div>

          <p className="text-center text-white/20 text-xs pb-8 px-6">
            {t.termsText}
          </p>
        </div>
      )}

      {/* ── GENDER STEP ── */}
      {step === 'gender' && (
        <div className="flex flex-col min-h-screen px-6">
          <div className="flex items-center justify-between mt-14 mb-8">
            <button onClick={() => setStep('landing')} className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition-colors">
              <AppImage src="/assets/images/app_logo.png" alt="MonerManush logo — go back" width={22} height={22} className="rounded-full object-cover opacity-70" />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            </button>
            <LanguageSwitcher />
          </div>

          <div className="flex-1 flex flex-col">
            <h2 className="font-brand text-4xl font-semibold mb-2" style={{ color: '#D4AF37' }}>{t.iAmA}</h2>
            <p className="text-white/40 text-sm mb-10">{t.genderPersonalize}</p>

            <div className="space-y-4 mb-10">
              {/* Woman card */}
              <button
                onClick={() => { setGender('woman'); setStep('auth'); setAuthMode('signup'); }}
                className={`w-full p-5 rounded-2xl border text-left transition-all duration-200 active:scale-[0.98] ${
                  gender === 'woman' ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl" style={{ background: 'rgba(155,28,28,0.2)' }}>👩</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-white text-base">{t.woman}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-[#0a0a0a]" style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D060)' }}>{t.womanFree}</span>
                    </div>
                    <p className="text-white/40 text-sm">{t.womanDesc}</p>
                  </div>
                </div>
              </button>

              {/* Man card */}
              <button
                onClick={() => { setGender('man'); setStep('auth'); setAuthMode('signup'); }}
                className={`w-full p-5 rounded-2xl border text-left transition-all duration-200 active:scale-[0.98] ${
                  gender === 'man' ? 'border-gold bg-gold/10' : 'border-white/10 bg-white/[0.03] hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl" style={{ background: 'rgba(212,175,55,0.1)' }}>👨</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-white text-base">{t.man}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full border border-white/20 text-white/50">{t.manFree}</span>
                    </div>
                    <p className="text-white/40 text-sm">{t.manDesc}</p>
                  </div>
                </div>
              </button>

              {/* Non-binary */}
              <button
                onClick={() => { setGender('woman'); setStep('auth'); setAuthMode('signup'); }}
                className="w-full p-5 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-white/20 text-left transition-all duration-200 active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl" style={{ background: 'rgba(100,100,200,0.1)' }}>🌈</div>
                  <div className="flex-1">
                    <span className="font-semibold text-white text-base">{t.nonBinary}</span>
                    <p className="text-white/40 text-sm mt-0.5">{t.nonBinaryDesc}</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AUTH STEP ── */}
      {step === 'auth' && (
        <div className="flex flex-col min-h-screen px-6">
          <div className="flex items-center justify-between mt-14 mb-8">
            <button onClick={() => setStep('gender')} className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition-colors">
              <AppImage src="/assets/images/app_logo.png" alt="MonerManush logo — go back" width={22} height={22} className="rounded-full object-cover opacity-70" />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
            </button>
            <LanguageSwitcher />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-6">
              <AppImage
                src="/assets/images/MM_best_2-1773496555810.jpeg"
                alt="MonerManush logo"
                width={40}
                height={40}
                className="rounded-xl object-cover"
              />
              <h2 className="font-brand text-3xl font-semibold" style={{ color: '#D4AF37' }}>
                {authMode === 'login' ? t.welcomeBack : t.createAccount}
              </h2>
            </div>

            <form onSubmit={handleAuthSubmit(onAuthSubmit)} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/50 mb-2 tracking-wide uppercase">{t.emailLabel}</label>
                <input
                  type="email"
                  className="input-dark"
                  placeholder={t.emailPlaceholder}
                  {...registerAuth('email', {
                    required: t.emailRequired,
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t.emailInvalid }
                  })}
                />
                {authErrors.email && <p className="text-red-400 text-xs mt-1.5">{authErrors.email.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-white/50 mb-2 tracking-wide uppercase">{t.passwordLabel}</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-dark pr-12"
                    placeholder="Min. 8 characters"
                    {...registerAuth('password', {
                      required: t.passwordRequired,
                      minLength: { value: 8, message: t.passwordMin }
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
                {authErrors.password && <p className="text-red-400 text-xs mt-1.5">{authErrors.password.message}</p>}
              </div>

              {authMode === 'login' && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-white/5 accent-gold" />
                    <span className="text-white/40 text-sm">Remember me</span>
                  </label>
                  <button type="button" className="text-gold text-sm hover:text-gold-light transition-colors">{t.forgotPassword}</button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-2xl font-semibold text-base tracking-wide transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D060 40%, #C9981A 100%)', color: '#0a0a0a' }}
              >
                {loading ? (
                  <><svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> {authMode === 'login' ? 'Signing in…' : 'Creating account…'}</>
                ) : (
                  authMode === 'login' ? t.signIn : t.signUp
                )}
              </button>
            </form>

            <p className="text-center text-white/30 text-sm mt-6">
              {authMode === 'login' ? (
                <>{t.noAccount.split('?')[0]}? <button onClick={() => setAuthMode('signup')} className="text-gold hover:text-gold-light transition-colors font-medium">{t.noAccount.split('? ')[1]}</button></>
              ) : (
                <>{t.haveAccount.split('?')[0]}? <button onClick={() => setAuthMode('login')} className="text-gold hover:text-gold-light transition-colors font-medium">{t.haveAccount.split('? ')[1]}</button></>
              )}
            </p>

            {authMode === 'signup' && (
              <p className="text-center text-white/20 text-xs mt-4 px-4">
                {t.termsText}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── ONBOARDING: NAME ── */}
      {step === 'onboarding-name' && (
        <div className="flex flex-col min-h-screen px-6">
          <div className="mt-14 mb-4">
            <div className="flex gap-1 mb-8">
              {['onboarding-name','onboarding-dob','onboarding-photos','onboarding-bio'].map((s,i) => (
                <div key={s} className="flex-1 h-1 rounded-full" style={{ background: i === 0 ? 'linear-gradient(135deg, #D4AF37, #C9981A)' : 'rgba(255,255,255,0.1)' }} />
              ))}
            </div>
          </div>
          <div className="flex-1">
            <h2 className="font-brand text-4xl font-semibold mb-2" style={{ color: '#D4AF37' }}>{t.whatsYourName}</h2>
            <p className="text-white/40 text-sm mb-8">{t.nameDesc}</p>
            <form onSubmit={handleProfileSubmit(() => setStep('onboarding-dob'))}>
              <div className="mb-6">
                <label className="block text-xs font-medium text-white/50 mb-2 tracking-wide uppercase">{t.displayName}</label>
                <input
                  type="text"
                  className="input-dark text-xl font-brand"
                  placeholder={t.namePlaceholder}
                  {...registerProfile('name', { required: t.nameRequired, minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
                />
                {profileErrors.name && <p className="text-red-400 text-xs mt-1.5">{profileErrors.name.message}</p>}
              </div>
              <button type="submit" className="w-full py-4 rounded-2xl font-semibold text-base transition-all duration-200 active:scale-95" style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D060 40%, #C9981A 100%)', color: '#0a0a0a' }}>
                {t.continueBtn}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── ONBOARDING: DOB ── */}
      {step === 'onboarding-dob' && (
        <div className="flex flex-col min-h-screen px-6">
          <div className="mt-14 mb-4">
            <div className="flex gap-1 mb-8">
              {['onboarding-name','onboarding-dob','onboarding-photos','onboarding-bio'].map((s,i) => (
                <div key={s} className="flex-1 h-1 rounded-full" style={{ background: i <= 1 ? 'linear-gradient(135deg, #D4AF37, #C9981A)' : 'rgba(255,255,255,0.1)' }} />
              ))}
            </div>
          </div>
          <div className="flex-1">
            <h2 className="font-brand text-4xl font-semibold mb-2" style={{ color: '#D4AF37' }}>{t.whatsYourDob}</h2>
            <p className="text-white/40 text-sm mb-8">{t.dobDesc}</p>
            <div className="mb-6">
              <label className="block text-xs font-medium text-white/50 mb-2 tracking-wide uppercase">Date of Birth</label>
              <input
                type="date"
                className="input-dark"
                max="2008-01-01"
                {...registerProfile('dob', { required: 'Date of birth is required' })}
              />
              {profileErrors.dob && <p className="text-red-400 text-xs mt-1.5">{profileErrors.dob.message}</p>}
            </div>
            <button onClick={() => setStep('onboarding-photos')} className="w-full py-4 rounded-2xl font-semibold text-base transition-all duration-200 active:scale-95 flex items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D060 40%, #C9981A 100%)', color: '#0a0a0a' }}>
              <AppImage src="/assets/images/app_logo.png" alt="MonerManush logo" width={20} height={20} className="rounded-full object-cover" />
              {t.continueBtn}
            </button>
          </div>
        </div>
      )}

      {/* ── ONBOARDING: PHOTOS ── */}
      {step === 'onboarding-photos' && (
        <div className="flex flex-col min-h-screen px-6">
          <div className="mt-14 mb-4">
            <div className="flex gap-1 mb-8">
              {['onboarding-name','onboarding-dob','onboarding-photos','onboarding-bio'].map((s,i) => (
                <div key={s} className="flex-1 h-1 rounded-full" style={{ background: i <= 2 ? 'linear-gradient(135deg, #D4AF37, #C9981A)' : 'rgba(255,255,255,0.1)' }} />
              ))}
            </div>
          </div>
          <div className="flex-1">
            <h2 className="font-brand text-4xl font-semibold mb-2" style={{ color: '#D4AF37' }}>{t.addPhotos}</h2>
            <p className="text-white/40 text-sm mb-8">{t.photosDesc}</p>

            <div className="grid grid-cols-3 gap-3 mb-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <label key={i} className={`aspect-[3/4] rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-200 overflow-hidden relative ${
                  uploadedPhotos[i] ? '' : 'border-2 border-dashed border-white/15 hover:border-gold/40 bg-white/[0.02]'
                }`}>
                  {uploadedPhotos[i] ? (
                    <img src={uploadedPhotos[i]} alt={`Profile photo ${i + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.5)" strokeWidth="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      {i === 0 && <span className="text-white/30 text-xs">Main</span>}
                    </div>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} multiple={i === 0} />
                </label>
              ))}
            </div>

            <button
              onClick={() => setStep('onboarding-bio')}
              disabled={uploadedPhotos.length === 0}
              className="w-full py-4 rounded-2xl font-semibold text-base transition-all duration-200 active:scale-95 disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D060 40%, #C9981A 100%)', color: '#0a0a0a' }}
            >
              <AppImage src="/assets/images/app_logo.png" alt="MonerManush logo" width={20} height={20} className="rounded-full object-cover" />
              {t.continueBtn} {uploadedPhotos.length > 0 && `(${uploadedPhotos.length})`}
            </button>
            <button onClick={() => setStep('onboarding-bio')} className="w-full mt-3 py-3 text-white/30 text-sm hover:text-white/50 transition-colors">
              {t.skip}
            </button>
          </div>
        </div>
      )}

      {/* ── ONBOARDING: BIO ── */}
      {step === 'onboarding-bio' && (
        <div className="flex flex-col min-h-screen px-6">
          <div className="mt-14 mb-4">
            <div className="flex gap-1 mb-8">
              {['onboarding-name','onboarding-dob','onboarding-photos','onboarding-bio'].map((s) => (
                <div key={s} className="flex-1 h-1 rounded-full" style={{ background: 'linear-gradient(135deg, #D4AF37, #C9981A)' }} />
              ))}
            </div>
          </div>
          <div className="flex-1">
            <h2 className="font-brand text-4xl font-semibold mb-2" style={{ color: '#D4AF37' }}>{t.yourBio}</h2>
            <p className="text-white/40 text-sm mb-8">{t.bioDesc}</p>

            <div className="mb-4">
              <label className="block text-xs font-medium text-white/50 mb-2 tracking-wide uppercase">{t.bio}</label>
              <textarea
                className="input-dark resize-none"
                rows={5}
                placeholder={t.bioPlaceholder}
                {...registerProfile('bio')}
                style={{ height: 'auto', minHeight: '120px' }}
              />
            </div>

            <button
              onClick={finishOnboarding}
              disabled={loading}
              className="w-full py-4 rounded-2xl font-semibold text-base transition-all duration-200 active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D060 40%, #C9981A 100%)', color: '#0a0a0a' }}
            >
              {loading ? (
                <><svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Setting up…</>
              ) : (
                <><AppImage src="/assets/images/app_logo.png" alt="MonerManush logo" width={20} height={20} className="rounded-full object-cover" />{t.finishProfile}</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}